import { ChatReferenceImage } from "@indigo-components/components/Chat/ChatReferenceImage/ChatReferenceImage";
import { useEffect, useState } from "react";
import { getIdToken } from "~/auth/authToken";
import { getConfig } from "~/config/configProvider";
import { errorReporter } from "~/error-handling";
import type { ReferenceImage } from "~/pydantic_models/services/models/chat_blocks";
import type { ChatBlockRendererProps } from "./types";

function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch {
    return false;
  }
}

export function parseReferenceImages(
  data: Record<string, unknown> | null | undefined,
): ReferenceImage[] | null {
  if (
    !Array.isArray(data?.images) ||
    !data.images.length ||
    data.images.length > 3
  )
    return null;
  const images: ReferenceImage[] = [];
  for (const raw of data.images) {
    if (!raw || typeof raw !== "object") return null;
    const image = raw as Partial<ReferenceImage>;
    if (
      typeof image.s3_key !== "string" ||
      !image.s3_key.startsWith("chat-attachments/") ||
      typeof image.s3_bucket !== "string" ||
      !isHttpsUrl(image.source_url) ||
      typeof image.source_title !== "string" ||
      typeof image.caption !== "string" ||
      typeof image.alt_text !== "string"
    )
      return null;
    images.push(image as ReferenceImage);
  }
  return images;
}

function ReferenceImageCard({ image }: { image: ReferenceImage }) {
  const [imageUrl, setImageUrl] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const { s3_bucket: bucket, s3_key: key } = image;
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setImageUrl(undefined);
    async function resolve() {
      try {
        const token = await getIdToken();
        const base = getConfig().AGENT_CHAT_FUNCTION_URL.replace(/\/$/, "");
        const response = await fetch(`${base}/attachment/presign-read`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ keys: [{ s3_bucket: bucket, s3_key: key }] }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Image access failed");
        const payload: unknown = await response.json();
        if (
          payload &&
          typeof payload === "object" &&
          "urls" in payload &&
          payload.urls &&
          typeof payload.urls === "object" &&
          key in payload.urls
        ) {
          const url: unknown = payload.urls[key as keyof typeof payload.urls];
          if (!isHttpsUrl(url)) throw new Error("Invalid image URL");
          if (!controller.signal.aborted) setImageUrl(url);
        } else throw new Error("Image read URL missing");
      } catch {
        if (!controller.signal.aborted)
          errorReporter.reportMessage("Reference image access failed", {
            level: "warning",
            tags: { feature: "indy", stage: "reference_image_access" },
          });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void resolve();
    // The read URL lasts an hour. Refresh while a history view stays open.
    const timer = window.setTimeout(
      () => setAttempt((value) => value + 1),
      50 * 60 * 1000,
    );
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [bucket, key, attempt]);

  return (
    <ChatReferenceImage
      imageUrl={imageUrl}
      loading={loading}
      caption={image.caption}
      altText={image.alt_text}
      sourceTitle={image.source_title}
      sourceUrl={image.source_url}
      onRetry={() => setAttempt((value) => value + 1)}
      onLoadError={() =>
        errorReporter.reportMessage("Reference image display failed", {
          level: "warning",
          tags: { feature: "indy", stage: "reference_image_display" },
        })
      }
    />
  );
}

export function ReferenceImagesBlock({
  block,
  fallback,
}: ChatBlockRendererProps) {
  const images = parseReferenceImages(block.data);
  if (!images) return <>{fallback}</>;
  return (
    <div className="flex flex-col gap-2">
      {images.map((image) => (
        <ReferenceImageCard key={image.s3_key} image={image} />
      ))}
    </div>
  );
}
